import React from 'react'
import { useApp } from '../context/AppContext'
import { ApiError, Appointment, AppointmentEmail, Business, Review, CardAccount, ConflictError, Service, ValidationError, VersionMismatchError, api } from '../api/client'
import type { ClientDetail, Consent, Note, NotePhoto, PaymentMethod, PaymentRecord, Purchase, ToastInput } from '../types/app'
import { conflictMessage } from '../calendar/booking'
import { formatLongDay } from '../calendar/dates'
import { clientName, timeRange } from '../calendar/names'
import { emailLine, failedConfirmation, reviewLine } from './appointmentEmails'
import { emailToOffer } from './emailGivenOnline'
import { whatsappLink } from '../today/whatsapp'
import { whatsappNumber } from '../clients/phone'
import { isCurrentRequest } from '../clients/list'
import { formatPrice, minutesToTime, formatLength } from '../catalog/format'
import { isRedeemable } from '../packages/display'
import { PAYMENT_METHODS } from '../payments/methods'
import { canRequestDeposit, cancelDeposit, depositRequestedLine, depositToAsk, heldLine, takesPayment } from '../payments/deposit'
import { completedMessage, paymentLine } from '../payments/status'
import { SheetKeys } from '../payments/idempotency'
import { canMarkNoShow, undoNoShowRefusal } from '../calendar/noShow'
import { canTakeCardPayments, cardPaymentsShown } from '../payments/visibility'
import RungSheet from '../billing/RungSheet'
import { missedDeposit, noShowPrompted, rememberNoShowPrompt } from '../billing/noShowPrompt'
import { noShowLine } from '../billing/rungs'
import { useBilling } from '../billing/useBilling'
import '../billing/billing.css'
import AppointmentForm from './AppointmentForm'
import MoreMenu from './MoreMenu'
import type { MoreItem } from './MoreMenu'
import PayLinkSheet from './PayLinkSheet'
import RefundSheet from './RefundSheet'
import PaymentHistory from './PaymentHistory'
import ChargeCardSheet from './ChargeCardSheet'
import { cardLabel, usableCard } from '../payments/savedCard'
import { refundsToCard } from '../payments/refund'
import DiscountSheet from './DiscountSheet'
import { canTakeDiscount, discountProblem, discountRow } from './discount'
import Field from '../screens/settings/Field'
import { saveErrorMessage, showToast } from '../screens/settings/toast'
import { useDialog } from '../hooks/useDialog'
import TimePicker from './TimePicker'
import VisitPrice from './VisitPrice'
import LengthPicker from './LengthPicker'
import { treatmentLabel } from './treatment'
import { lengthSummary } from '../screens/settings/serviceForm'
import './Sheet.css'
import './Booking.css'
import '../screens/settings/Settings.css'
import { Loading } from '../ui/States'
import { Icon, IconButton } from '../ui/Icon'
import NotePhotoGrid from '../notes/NotePhotoGrid'
import AddNotePhoto from '../notes/AddNotePhoto'
import QueuedPhotoTiles from '../notes/QueuedPhotoTiles'
import { useNotePhotoQueue } from '../notes/useNotePhotoQueue'
import { photosLeft } from '../notes/notePhotos'

type Mode = 'view' | 'complete' | 'reschedule' | 'service' | 'cancel'

// RefundTarget is the payment a Refund sheet is open for. recordedMethod is
// set for a payment not taken through Stripe, whose refund is only recorded.
interface RefundTarget {
  paymentId: string
  maxPence: number
  recordedMethod?: PaymentMethod
  toCard: boolean
}

// paymentMethodPhrase reads naturally after "paid", e.g. "paid in cash".
const paymentMethodPhrase = (method: PaymentMethod): string =>
  method === 'cash' ? 'in cash' : method === 'card' ? 'by card' : 'by bank transfer'

export default function AppointmentSheet() {
  const { state, dispatch } = useApp()
  const id = state.appointmentId
  const [appointment, setAppointment] = React.useState<Appointment | null>(null)
  const [loadFailed, setLoadFailed] = React.useState(false)
  const [mode, setMode] = React.useState<Mode>('view')
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState('')
  const [conflict, setConflict] = React.useState<string | null>(null)
  const [payingOutstanding, setPayingOutstanding] = React.useState(false)
  const [cardAccount, setCardAccount] = React.useState<CardAccount | null>(null)
  const [business, setBusiness] = React.useState<Business | null>(null)
  // After a no-show on Free: what a deposit would have kept, once per
  // appointment, and the Payments sheet it opens.
  const { billing } = useBilling()
  const [noShowPrompt, setNoShowPrompt] = React.useState<string | null>(null)
  const [paymentsSheet, setPaymentsSheet] = React.useState(false)
  const [payLinkOpen, setPayLinkOpen] = React.useState(false)
  // The payment the Refund sheet is open for, else null.
  const [refunding, setRefunding] = React.useState<RefundTarget | null>(null)
  const refundOpen = refunding !== null
  const [chargeOpen, setChargeOpen] = React.useState(false)
  // The amount the charge sheet confirms, fixed when it opens: a charge from
  // the Complete step completes the visit, and what is owed then is nothing.
  const [chargeAmount, setChargeAmount] = React.useState(0)
  // And the card it names: the client reloads once the visit is completed,
  // and the open sheet must not depend on it.
  const [chargeCard, setChargeCard] = React.useState('')
  const chargePaid = React.useRef(false)
  const [discountOpen, setDiscountOpen] = React.useState(false)
  const [formOpen, setFormOpen] = React.useState(false)
  const [menuOpen, setMenuOpen] = React.useState(false)

  const [date, setDate] = React.useState('')
  const [start, setStart] = React.useState<number | null>(null)
  const [services, setServices] = React.useState<Service[] | null>(null)
  // The length a change of treatment is trying for (kept for Book anyway),
  // and the treatment whose lengths are open for choosing.
  const [pendingLengthId, setPendingLengthId] = React.useState<string | null>(null)
  const [pickingServiceId, setPickingServiceId] = React.useState<string | null>(null)
  const [reason, setReason] = React.useState('')
  const [refundDeposit, setRefundDeposit] = React.useState(false)
  // One set of idempotency keys per sheet open: pressing Complete or a
  // payment method again replays the first answer instead of charging twice.
  const keys = React.useRef(new SheetKeys()).current

  // Client preferences and the treatment note, both keyed off the
  // appointment's client. Failing to load either just hides that bit of
  // information — it must never block viewing or acting on the booking.
  const [client, setClient] = React.useState<ClientDetail | null>(null)
  const [purchases, setPurchases] = React.useState<Purchase[] | null>(null)
  const purchasesToken = React.useRef(0)
  const [note, setNote] = React.useState<Note | null>(null)
  const [noteLoaded, setNoteLoaded] = React.useState(false)
  const [addingNote, setAddingNote] = React.useState(false)
  const [noteText, setNoteText] = React.useState('')
  const [noteSaving, setNoteSaving] = React.useState(false)
  const [noteError, setNoteError] = React.useState('')
  // Photos not on the note yet: picked while writing it, or going up. One
  // bound to a saved note goes up even after the sheet closes; if it fails
  // then, a toast says so, as there is no tile left to say it.
  const photoQueue = useNotePhotoQueue(client?.first_name, () =>
    showToast(dispatch, "A photo didn't upload. Open the appointment to try again.", { tone: 'error' }),
  )
  const clearPhotoQueue = photoQueue.clear
  // The appointment on show now, for a save that finishes after she moved on.
  const shownId = React.useRef(id)
  shownId.current = id

  const close = () => dispatch({ type: 'CLOSE_SHEET' })
  const dialogRef = React.useRef<HTMLDivElement>(null)
  useDialog(dialogRef, close, { closable: !busy && !payLinkOpen && !chargeOpen && !refundOpen && !discountOpen && !formOpen && !menuOpen })

  // Resolves with the appointment as loaded (null when it could not be).
  const load = React.useCallback(async (): Promise<Appointment | null> => {
    if (!id) {
      return null
    }
    setLoadFailed(false)
    try {
      const fresh = await api.getAppointment(id)
      setAppointment(fresh)
      return fresh
    } catch {
      setLoadFailed(true)
      return null
    }
  }, [id])

  React.useEffect(() => {
    load()
  }, [load])

  // The sheet opens before the appointment arrives; once it has, move focus
  // from the empty sheet to its heading (the client's name).
  const loaded = appointment !== null
  React.useEffect(() => {
    const node = dialogRef.current
    if (!loaded || !node || document.activeElement !== node) return
    const heading = node.querySelector<HTMLElement>('h2')
    if (heading) {
      heading.setAttribute('tabindex', '-1')
      heading.focus({ preventScroll: true })
    }
  }, [loaded])

  // Whether card payments are switched on decides if the pay-link button is
  // offered. Asked once per sheet open; a failure just hides the button.
  React.useEffect(() => {
    let live = true
    api.cardAccount().then((account) => { if (live) setCardAccount(account) }).catch(() => {})
    // Her deposit rule decides whether "Email deposit link" is offered.
    api.getBusiness().then((b) => { if (live) setBusiness(b) }).catch(() => {})
    return () => { live = false }
  }, [])

  const clientId = appointment?.client?.id ?? null
  const appointmentStatus = appointment?.status ?? null

  // From the Calendar or Today, the client is one tap away; on her own
  // profile the sheet already sits over it, so the name stays plain.
  const onClientProfile = state.activeTab === 'clients' && state.openClientId === clientId
  const openClient = () => {
    if (!clientId) return
    close()
    dispatch({ type: 'OPEN_CLIENT', payload: clientId })
    dispatch({ type: 'SET_ACTIVE_TAB', payload: 'clients' })
  }

  React.useEffect(() => {
    setClient(null)
    setNote(null)
    setNoteLoaded(false)
    setAddingNote(false)
    setNoteText('')
    setNoteError('')
    clearPhotoQueue()
    if (!clientId) {
      return
    }
    api.getClient(clientId).then(setClient).catch(() => {})
    // A note can be written ahead of a visit as well as after it (feedback 2026-10-11).
    if (appointmentStatus === 'completed' || appointmentStatus === 'booked') {
      api
        .listNotes(clientId)
        .then((notes) => setNote(notes.find((n) => n.appointment_id === id) ?? null))
        .catch(() => {})
        .finally(() => setNoteLoaded(true))
    }
  }, [clientId, appointmentStatus, id, clearPhotoQueue])

  // "Email given online": the booking page's email is offered to her, never
  // applied; she saves it to the client (or replaces the one on file).
  const [emailConfirming, setEmailConfirming] = React.useState(false)
  const [emailSaving, setEmailSaving] = React.useState(false)
  const [emailSaved, setEmailSaved] = React.useState(false)
  const [emailError, setEmailError] = React.useState('')
  React.useEffect(() => {
    setEmailConfirming(false)
    setEmailSaved(false)
    setEmailError('')
  }, [id])
  const saveClientEmail = async (email: string) => {
    if (!client) return
    setEmailSaving(true)
    setEmailError('')
    try {
      const updated = await api.updateClient(client.id, { email }, client.version)
      setClient((c) => (c ? { ...c, email: updated.email ?? email, email_confirmed: updated.email_confirmed } : c))
      setEmailConfirming(false)
      setEmailSaved(true)
    } catch (err) {
      setEmailError(saveErrorMessage(err))
    } finally {
      setEmailSaving(false)
    }
  }

  const [emails, setEmails] = React.useState<AppointmentEmail[]>([])
  const [review, setReview] = React.useState<Review | null>(null)
  const loadMail = React.useCallback(() => {
    if (id) api.appointmentMail(id).then((m) => { setEmails(m.emails); setReview(m.review) }).catch(() => {})
  }, [id])
  React.useEffect(() => {
    setEmails([])
    setReview(null)
    loadMail()
  }, [loadMail])

  // The client's package purchases, reloaded after a redemption conflict so
  // a credit someone else just spent disappears from the "pay with a
  // package" option instead of staying offered.
  const loadPurchases = React.useCallback(() => {
    const token = ++purchasesToken.current
    setPurchases(null)
    if (!clientId) {
      return
    }
    api
      .clientPackages(clientId)
      .then((list) => {
        if (isCurrentRequest(token, purchasesToken.current)) setPurchases(list)
      })
      .catch(() => {
        if (isCurrentRequest(token, purchasesToken.current)) setPurchases(null)
      })
  }, [clientId])

  React.useEffect(() => {
    loadPurchases()
  }, [loadPurchases])

  const openMode = (next: Mode) => {
    setMode(next)
    setError('')
    setConflict(null)
    setPayingOutstanding(false)
    if (next === 'reschedule' && appointment) {
      setDate(appointment.date)
      setStart(appointment.start_minute)
    }
    if (next === 'service') {
      // Her booked treatment opens with its lengths shown, the booked one chosen.
      setPickingServiceId(appointment?.service.id ?? null)
      setPendingLengthId(null)
    }
    if (next === 'service' && services === null) {
      api
        .listServices()
        .then(setServices)
        .catch(() => setError("Couldn’t load your treatments. Check your connection and try again."))
    }
    if (next === 'cancel') {
      setReason('')
      setRefundDeposit(false)
    }
  }

  // run makes one change. Success closes the sheet with a toast; problems stay
  // on the sheet so Jessica can fix them. request names an idempotent request
  // (see SheetKeys) so a failure decides whether its key is reused.
  const run = async (
    action: () => Promise<Appointment>,
    message: (updated: Appointment) => string | ToastInput,
    appointmentStateMessage = 'This appointment has already been completed or cancelled.',
    proposed?: { date: string; start_minute: number },
    request?: string,
    // stayOpen keeps the sheet open on the updated appointment when it
    // has something more to say (the no-show prompt).
    stayOpen?: (updated: Appointment) => boolean,
  ) => {
    setBusy(true)
    setError('')
    setConflict(null)
    try {
      const updated = await action()
      dispatch({ type: 'APPOINTMENTS_CHANGED' })
      const said = message(updated)
      dispatch({ type: 'SHOW_TOAST', payload: said })
      if (stayOpen?.(updated)) {
        setAppointment(updated)
        setMode('view')
        setBusy(false)
        return
      }
      close()
    } catch (e) {
      if (request) keys.failed(request, e)
      if (e instanceof ConflictError) {
        setConflict(conflictMessage(e.conflicts, proposed, e.blocks))
      } else if (e instanceof ApiError && e.problem === 'appointment-not-started') {
        setError("This appointment hasn’t started yet.")
      } else if (e instanceof ApiError && e.problem === 'no-package-credit') {
        // Someone else redeemed the last credit first (another device, or a
        // retried request). Reload so the option disappears instead of
        // staying offered for a credit that no longer exists.
        setError('That package session is no longer available.')
        loadPurchases()
      } else if (e instanceof VersionMismatchError || (e instanceof ApiError && e.problem === 'appointment-state')) {
        setError(
          e instanceof VersionMismatchError
            ? 'This appointment changed on another device. Check it and try again.'
            : appointmentStateMessage,
        )
        setMode('view')
        dispatch({ type: 'APPOINTMENTS_CHANGED' })
        await load()
      } else if (e instanceof ValidationError) {
        setError(Object.values(e.fields)[0] ?? 'Check the details and try again.')
      } else {
        setError(saveErrorMessage(e))
      }
      setBusy(false)
    }
  }

  if (!appointment) {
    return (
      <div className="sheet-overlay" onClick={() => { if (!busy) close() }}>
        <div ref={dialogRef} className="sheet-content sheet-content--closable" role="dialog" aria-modal="true" aria-label="Appointment" onClick={(e) => e.stopPropagation()}>
          <div className="sheet-handle"></div>
          <IconButton icon="close" label="Close" className="sheet-close" onClick={close} disabled={busy} />
          <div className="sheet-body">
            {loadFailed ? (
              <div role="alert">
                <p>Couldn’t load this appointment. Check your connection and try again.</p>
                <button type="button" className="btn btn-secondary btn-compact" onClick={load}>
                  Try again
                </button>
              </div>
            ) : (
              <Loading />
            )}
          </div>
        </div>
      </div>
    )
  }

  const a = appointment
  const held = a.awaiting_deposit
  const booked = a.status === 'booked'
  const bookedLengthId = a.service.service_length_id
  const canUsePackageCredit =
    !!bookedLengthId && !!purchases?.some((p) => isRedeemable(p) && p.service_length_ids.includes(bookedLengthId))

  // A booking a package session pays for can only move to a length the
  // package covers: the server keeps the package on it, and Complete would
  // then fail (A1 hand-off, known gap). Others are shown but not offered.
  const packageLengths = a.package ? purchases?.find((p) => p.id === a.package?.purchase_id)?.service_length_ids : undefined
  const notInPackage = (lengthId: string) =>
    !!packageLengths && lengthId !== bookedLengthId && !packageLengths.includes(lengthId)
  const serviceNotInPackage = (s: Service) => s.lengths.every((l) => notInPackage(l.id))

  // What Complete still has to take (an older API: what is owed).
  const due = a.amount_due_pence ?? a.owed_pence
  const complete = (paid?: PaymentMethod | 'not_yet' | 'package') => {
    const request = `complete:${paid ?? 'nothing-due'}`
    return run(
      () => api.completeAppointment(a.id, a.version, paid, keys.key(request)),
      (u) => {
        if (paid === undefined) return completedMessage(u)
        if (paid === 'package') return 'Completed · paid with a package'
        if (paid === 'not_yet') return `Completed · ${formatPrice(due)} to pay`
        return `Completed · ${formatPrice(due)} paid ${paymentMethodPhrase(paid)}`
      },
      undefined,
      undefined,
      request,
    )
  }

  // On Free, the first no-show of an appointment says what her deposit
  // policy would have kept (spec 1.7), and the sheet stays open to say it.
  const offerDeposit = (u: Appointment): boolean => {
    if (billing?.rung !== 'free' || noShowPrompted(u.id)) return false
    rememberNoShowPrompt(u.id)
    setNoShowPrompt(noShowLine(missedDeposit(business, u.total_price_pence)))
    return true
  }

  // Didn't come: keeps any deposit, emails nobody, and can be undone from the toast.
  const markNoShow = () =>
    run(
      () => api.markNoShow(a.id, a.version),
      (u) => ({
        message: `${a.client?.first_name ?? 'Appointment'} marked as no-show`,
        action: {
          label: 'Undo',
          run: () => {
            api
              .undoNoShow(u.id, u.version)
              .then(() => {
                dispatch({ type: 'APPOINTMENTS_CHANGED' })
                showToast(dispatch, 'Back to booked')
              })
              .catch((err) => {
                showToast(dispatch, undoNoShowRefusal(err) ?? saveErrorMessage(err), { tone: 'error' })
              })
          },
        },
      }),
      undefined,
      undefined,
      undefined,
      offerDeposit,
    )

  // Settling by hand needs a completed booking: outstanding, or part paid
  // once its balance is due.
  const settleable = a.status === 'completed' && (a.payment_status === 'outstanding' || a.payment_status === 'part_paid')
  const canTakeCard =
    canTakeCardPayments(cardAccount) &&
    (a.status === 'booked' || a.status === 'completed') &&
    takesPayment(a)
  const hasOpenLink = a.card_checkout?.status === 'open'
  // The server decides whether a saved card may be charged (can_charge_card);
  // the card is named from the client's profile.
  const savedCard = client?.card
  // On her Complete step a booked visit may be charged before its start
  // time: tapping Charge says it happened, and the charge completes it. A pay
  // link from the same step completes it too, as the link is made.
  const chargeCompletes = mode === 'complete' && a.status === 'booked'
  const canChargeCard =
    canTakeCardPayments(cardAccount) && usableCard(savedCard) && (a.can_charge_card || (chargeCompletes && a.can_charge_card_on_complete))
  // Whether the price line says anything about payment after the price.
  const paymentShown = !!depositRequestedLine(a) || paymentLine(a) !== ''
  // A refund goes against one payment, so its own amount less its pending
  // and succeeded refunds is the most that can be given back. A Stripe card
  // payment needs card payments shown to refund; one she took herself (cash,
  // bank transfer, her own reader) she can always record.
  const refundable = (p: PaymentRecord) => p.refundable_pence > 0 && (!p.through_stripe || cardPaymentsShown(cardAccount))
  const refundTarget = (p: PaymentRecord): RefundTarget => ({
    paymentId: p.id,
    maxPence: p.refundable_pence,
    recordedMethod: p.through_stripe ? undefined : p.method,
    toCard: refundsToCard(p),
  })
  // The Refund action refunds the payment the API names: the latest with
  // something left. Each payment in the history has its own Refund too.
  const refundPayment = a.payments.find((p) => p.id === a.refund_payment_id)
  const canRefund = !!refundPayment && refundable(refundPayment)
  const openRefund = () => refundPayment && setRefunding(refundTarget(refundPayment))
  // Opened from the Complete step, a pay link completes the visit: once it
  // has, the sheet leaves the step (no "£0 to pay" behind a paid link). A link
  // that was never made completed nothing, and she stays on the step.
  const reloadAfterPayLink = () => {
    const fromComplete = mode === 'complete'
    load().then((fresh) => {
      if (fromComplete && fresh && fresh.status !== 'booked') setMode('view')
    })
  }

  // A deposit link for a booking she made: emailed to the client, paid from
  // their manage page. Offered once the client has an email address.
  const depositAsk = a.deposit_pence ?? depositToAsk(business, a.total_price_pence)
  const clientEmail = client?.email ?? a.contact_email
  // Cancelling inside her notice keeps a paid deposit unless she ticks
  // "Refund the deposit instead"; outside it, it is refunded.
  const depositOnCancel = cancelDeposit(a, refundDeposit)
  const canAskDeposit =!!business?.deposits_active && depositAsk > 0 && !!clientEmail && canRequestDeposit(a)
  const askDeposit = () =>
    run(
      () => api.requestDeposit(a.id),
      () => `Deposit link emailed to ${a.client?.first_name ?? 'the client'}`,
    )

  const removeDiscount = async () => {
    setBusy(true)
    setError('')
    try {
      await api.clearDiscount(a.id, a.version)
      dispatch({ type: 'APPOINTMENTS_CHANGED' })
      await load()
    } catch (e) {
      if (e instanceof VersionMismatchError || (e instanceof ApiError && e.problem === 'appointment-state')) {
        staleDiscount(e instanceof VersionMismatchError ? 'This booking changed on another device. Check it and try again.' : "This booking can't take a discount any more.")
      } else if (e instanceof ApiError && e.problem === 'discount-below-paid') {
        setError(discountProblem(e, a.paid_pence))
      } else {
        setError(saveErrorMessage(e))
      }
    }
    setBusy(false)
  }
  // "Use their package" / "Don't use the package" on a booked visit: whether a
  // package session pays for it. The sheet stays open and reloads.
  const changePackage = async (use: boolean) => {
    setBusy(true)
    setError('')
    try {
      const updated = use ? await api.linkPackage(a.id, a.version) : await api.unlinkPackage(a.id, a.version)
      dispatch({ type: 'APPOINTMENTS_CHANGED' })
      showToast(dispatch, use ? `Using their ${updated.package?.name ?? 'package'} package` : 'Not using the package')
      setAppointment(updated)
      loadPurchases()
    } catch (e) {
      if (e instanceof ValidationError) {
        setError(Object.values(e.fields)[0] ?? 'Check the details and try again.')
      } else if (e instanceof VersionMismatchError || (e instanceof ApiError && e.problem === 'appointment-state')) {
        setError(
          e instanceof VersionMismatchError
            ? 'This appointment changed on another device. Check it and try again.'
            : 'This booking can’t change its package any more.',
        )
        dispatch({ type: 'APPOINTMENTS_CHANGED' })
        await load()
      } else {
        setError(saveErrorMessage(e))
      }
    }
    setBusy(false)
  }
  const packageAction = booked && !held ? (a.package ? 'unlink' : a.package_available ? 'link' : null) : null

  const staleDiscount = (message: string) => {
    setDiscountOpen(false)
    setError(message)
    dispatch({ type: 'APPOINTMENTS_CHANGED' })
    load()
  }

  // Releasing a hold gives the time back. A 409 means the deposit arrived
  // first, so the sheet reloads as the paid booking it now is.
  const release = async () => {
    setBusy(true)
    setError('')
    try {
      await api.releaseHold(a.id)
      dispatch({ type: 'APPOINTMENTS_CHANGED' })
      showToast(dispatch, 'Time released')
      close()
    } catch (e) {
      if (e instanceof ApiError && e.problem === 'appointment-state') {
        setError('This appointment has just been paid.')
        dispatch({ type: 'APPOINTMENTS_CHANGED' })
        await load()
      } else {
        setError(saveErrorMessage(e))
      }
      setBusy(false)
    }
  }

  const reschedule = (force: boolean) => {
    if (start === null) {
      setError('Choose a start time.')
      return
    }
    run(
      () => api.changeAppointment(a.id, a.version, { date, start_minute: start, force }),
      (u) => (u.outside_hours ? 'Moved — outside your working hours' : 'Moved'),
      'This appointment has already been completed or cancelled.',
      { date, start_minute: start },
    )
  }

  // The toast names the length when the treatment has several.
  const changedLabel = (u: Appointment) => {
    const count = services?.find((s) => s.id === u.service.id)?.lengths.length ?? 1
    return treatmentLabel(u.service.name, u.service.duration_minutes, count)
  }
  const changeLength = (lengthId: string, force: boolean) => {
    setPendingLengthId(lengthId)
    run(
      () => api.changeAppointment(a.id, a.version, { service_length_id: lengthId, force }),
      (u) => `Changed to ${changedLabel(u)}`,
      'This appointment has already been completed or cancelled.',
      { date: a.date, start_minute: a.start_minute },
    )
  }

  const startAddNote = () => {
    setNoteText('')
    setNoteError('')
    setAddingNote(true)
  }

  const saveNote = async () => {
    if (!client) {
      return
    }
    setNoteSaving(true)
    setNoteError('')
    // Pressing Save note commits the photos picked so far to this note: they
    // go up once it exists, even if the sheet has closed or moved on by then.
    const held = photoQueue.waiting()
    try {
      const saved = await api.createNote(client.id, { text: noteText, appointment_id: a.id })
      photoQueue.upload(client.id, saved.id, notePhotoAdded(saved.id), held)
      if (shownId.current !== a.id) return
      setNote(saved)
      setAddingNote(false)
    } catch (e) {
      if (shownId.current !== a.id) return
      if (e instanceof ValidationError) {
        setNoteError(Object.values(e.fields)[0] ?? 'Check the note and try again.')
      } else if (e instanceof ApiError && e.problem === 'note-exists') {
        // Someone else already added a note for this appointment (another
        // device, or a retried request). Show it instead of a generic
        // failure — there is nothing left to fix by retrying the save.
        setAddingNote(false)
        showToast(dispatch, 'This appointment already has notes')
        api
          .listNotes(client.id)
          .then((notes) => {
            const found = notes.find((n) => n.appointment_id === a.id) ?? null
            // The photos she picked still belong with this appointment.
            if (found) photoQueue.upload(client.id, found.id, notePhotoAdded(found.id), held)
            if (shownId.current !== a.id) return
            setNote(found)
            if (!found) photoQueue.clear()
          })
          .catch(() => {})
      } else {
        setNoteError(saveErrorMessage(e))
      }
    } finally {
      setNoteSaving(false)
    }
  }

  // An uploaded photo joins its note, unless the sheet has moved to another,
  // or the note was reloaded with it already there.
  const notePhotoAdded = (noteId: string) => (photo: NotePhoto) =>
    setNote((n) =>
      n && n.id === noteId && !(n.photos ?? []).some((p) => p.id === photo.id) ? { ...n, photos: [...(n.photos ?? []), photo] } : n,
    )
  const notePhotoRemoved = (photoId: string) =>
    setNote((n) => (n ? { ...n, photos: (n.photos ?? []).filter((p) => p.id !== photoId) } : n))
  // Agreement recorded from the note goes on the client, as on the profile.
  const photoConsentRecorded = (consent: Consent) =>
    setClient((c) => (c ? { ...c, consent: [consent, ...c.consent] } : c))
  const businessName = state.me?.business?.trading_name ?? 'us'

  // Complete's payment step, and settling a completed visit: what is left to
  // pay, the card pay link first, then the methods side by side.
  const payStep = (amount: number, pay: (method: PaymentMethod) => void, more?: React.ReactNode) => (
    <section className="booking-section">
      <h3 className="booking-label">How did they pay?</h3>
      <p className="pay-step__due">{formatPrice(amount)} to pay</p>
      <div className="pay-step__options">
        {canChargeCard && usableCard(savedCard) && (
          <button type="button" className="btn btn-primary btn-block" disabled={busy} onClick={() => {
            chargePaid.current = false
            setChargeAmount(amount)
            setChargeCard(cardLabel(savedCard))
            setChargeOpen(true)
          }}>
            Charge {cardLabel(savedCard)}
          </button>
        )}
        {canTakeCard && (
          <button type="button" className={canChargeCard ? 'btn btn-secondary btn-block' : 'btn btn-primary btn-block'} disabled={busy} onClick={() => setPayLinkOpen(true)}>
            {hasOpenLink ? 'Show pay link' : canChargeCard ? 'Send a pay link instead' : 'Card · pay link'}
          </button>
        )}
        <div className="pay-step__methods">
          {PAYMENT_METHODS.map(({ method, label }) => (
            <button key={method} type="button" className="btn btn-secondary" disabled={busy} onClick={() => pay(method)}>
              {label}
            </button>
          ))}
        </div>
        {more}
      </div>
    </section>
  )

  // The sheet's main action: Complete (with what is left to take), or Take
  // payment once a completed visit is unpaid. Nothing paid by pay link,
  // package, full deposit or free completes in one tap.
  const main = booked ? (
    <button type="button" className="btn btn-primary" onClick={() => (due === 0 ? complete() : openMode('complete'))} disabled={busy}>
      {due > 0 ? `Complete · ${formatPrice(due)}` : 'Complete'}
    </button>
  ) : settleable && !payingOutstanding ? (
    <button type="button" className="btn btn-primary" disabled={busy} onClick={() => setPayingOutstanding(true)}>
      Take payment · {formatPrice(a.owed_pence)}
    </button>
  ) : null

  // More: change it, add to it, it didn't happen or is to be paid back, and
  // cancelling last.
  const canAddNote = (a.status === 'completed' || booked) && !!client && noteLoaded && !note && !addingNote
  const more: MoreItem[][] = [
    booked
      ? [
          { label: 'Change time', onSelect: () => openMode('reschedule') },
          { label: 'Change treatment', onSelect: () => openMode('service') },
        ]
      : [],
    [
      ...(canTakeDiscount(a) && !discountRow(a) ? [{ label: 'Add discount', onSelect: () => setDiscountOpen(true) }] : []),
      ...(canAddNote ? [{ label: 'Add appointment note', onSelect: startAddNote }] : []),
    ],
    [
      ...(booked && canMarkNoShow(a) ? [{ label: 'Didn’t come', onSelect: markNoShow }] : []),
      ...(canAskDeposit
        ? [{ label: a.deposit_pence ? `Email deposit link again · ${formatPrice(depositAsk)}` : `Email a ${formatPrice(depositAsk)} deposit link`, onSelect: askDeposit }]
        : []),
      ...(canRefund ? [{ label: 'Refund', onSelect: openRefund, danger: true }] : []),
    ],
    booked ? [{ label: 'Cancel appointment', onSelect: () => openMode('cancel'), danger: true }] : [],
  ]

  return (
    <div className="sheet-overlay" onClick={() => { if (!busy) close() }}>
      <div ref={dialogRef} className="sheet-content sheet-content--closable" role="dialog" aria-modal="true" aria-label={`Appointment with ${clientName(a.client)}`} onClick={(e) => e.stopPropagation()}>
        <div className="sheet-handle"></div>
        <IconButton icon="close" label="Close" className="sheet-close" onClick={close} disabled={busy} />
        <div className="sheet-header sheet-header--closable">
          <small>
            {formatLongDay(a.date)} · {timeRange(a)}
          </small>
          <h2>
            {a.client && !onClientProfile ? (
              <button type="button" className="appt-client-link" onClick={openClient} disabled={busy}>
                {clientName(a.client)}
                <Icon name="chevron-right" size={20} />
              </button>
            ) : (
              clientName(a.client)
            )}
          </h2>
          <p>
            {a.service.name} · {formatLength(a.service.duration_minutes)}
          </p>
          {a.blocked_until_minute > a.end_minute && (
            <p className="appt-blocked">Room blocked until {minutesToTime(a.blocked_until_minute % 1440)}</p>
          )}
          {/* Important to know: every item, health first, never behind a
              toggle (review 2026-10-14) — an allergy not ticked as health
              must still be in sight. */}
          {client && client.preferences.length > 0 && (
            <section className="appt-preferences" aria-label="Important to know">
              <h3 className="booking-label">Important to know</h3>
              <ul className="appt-preferences__list">
                {importantFirst(client.preferences).map((p) => (
                  <li key={p.id} className="appt-preferences__item">
                    {p.is_health_flag && <span className="tag tag--warning">Health</span>}
                    <span className="appt-preferences__text">
                      <strong>{p.label}</strong>
                      {p.value ? `: ${p.value}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        <div className="sheet-body booking-form">
          {noShowPrompt && (
            <div className="no-show-prompt">
              <button type="button" className="no-show-prompt__text" aria-haspopup="dialog" onClick={() => setPaymentsSheet(true)}>
                <span>{noShowPrompt}</span>
                <Icon name="chevron-right" size={16} />
              </button>
              <IconButton icon="close" label="Dismiss" onClick={() => setNoShowPrompt(null)} />
            </div>
          )}
          {paymentsSheet && <RungSheet rung="card_payments" lock="no_show" onClose={() => setPaymentsSheet(false)} />}
          {held && (
            <div className="appt-detail-item">
              <span className="detail-label">Deposit</span>
              <span className="detail-value">{heldLine(a, state.me?.professional.timezone)}</span>
            </div>
          )}
          {!held && mode === 'view' && (
            <div>
              {a.source === 'booking_page' && (
                <div className="appt-detail-item">
                  <span className="detail-label">Booked</span>
                  <span className="detail-value">
                    Online{a.booked_as_name ? ` as ${a.booked_as_name}` : ''}
                  </span>
                </div>
              )}
              {a.contact_email && client && (() => {
                const offer = emailToOffer(a.contact_email, client.email, client.email_confirmed ?? false)
                if (!offer && !emailSaved) return null
                return (
                  <div className="appt-detail-item">
                    <span className="detail-label">Email given online</span>
                    <span className="detail-value">{a.contact_email}</span>
                    {offer && !emailConfirming && (
                      <button type="button" className="btn btn-quiet btn-compact" disabled={emailSaving}
                        onClick={() => (offer.replace ? setEmailConfirming(true) : saveClientEmail(offer.email))}>
                        {offer.replace ? 'Replace client email' : 'Save to client'}
                      </button>
                    )}
                    {offer && emailConfirming && (
                      <span>
                        <span className="field-hint">Replace {client.email} with {offer.email}?</span>{' '}
                        <button type="button" className="btn btn-quiet btn-compact" disabled={emailSaving} onClick={() => saveClientEmail(offer.email)}>
                          Replace
                        </button>{' '}
                        <button type="button" className="btn btn-quiet btn-compact" disabled={emailSaving} onClick={() => setEmailConfirming(false)}>
                          Cancel
                        </button>
                      </span>
                    )}
                    {!offer && emailSaved && <span className="field-hint" role="status">Saved</span>}
                    {emailError && <p className="field-error" role="alert">{emailError}</p>}
                  </div>
                )
              })()}
              {emailLine(emails) && (
                <div className="appt-detail-item">
                  <span className="detail-label">Email</span>
                  <span className="detail-value">{emailLine(emails)}</span>
                </div>
              )}
              {reviewLine(review) && (
                <div className="appt-detail-item">
                  <span className="detail-label">Review</span>
                  <span className="detail-value">{reviewLine(review)}</span>
                </div>
              )}
              {failedConfirmation(emails) && client?.phone && (
                <div className="appt-detail-item">
                  <span className="detail-value">The confirmation email didn’t send.</span>
                  <a className="btn btn-secondary btn-compact" href={whatsappLink(whatsappNumber(client.phone), confirmationText(a))} target="_blank" rel="noreferrer">
                    Send it on WhatsApp
                  </a>
                </div>
              )}
              <AppointmentForm appointment={a} onOverlayChange={setFormOpen} />
              {/* Price and what's been paid on one line (design review P11). */}
              <div className="appt-detail-item">
                <span className="detail-label">Price</span>
                <span className="detail-value">
                  <VisitPrice pence={a.total_price_pence} paymentState={a.payment_state} />
                  {paymentShown ? ' · ' : null}
                  {depositRequestedLine(a) ? (
                    depositRequestedLine(a)
                  ) : a.disputed || a.dispute_outcome === 'lost' ? (
                    <span className="tag tag--danger">{paymentLine(a)}</span>
                  ) : a.paid_twice && canRefund ? (
                    <button type="button" className="btn btn-quiet btn-compact" onClick={openRefund}>
                      {paymentLine(a)}
                    </button>
                  ) : (
                    paymentLine(a)
                  )}
                </span>
              </div>
              {/* Whether a package session pays for a booked visit, quietly changeable. */}
              {packageAction && (
                <div className="appt-detail-item">
                  <span className="detail-label">Package</span>
                  <span className="detail-value">
                    {a.package ? `${a.package.name} · ` : null}
                    <button type="button" className="btn btn-quiet btn-compact" disabled={busy} onClick={() => changePackage(packageAction === 'link')}>
                      {packageAction === 'link' ? 'Use their package' : 'Don’t use the package'}
                    </button>
                  </span>
                </div>
              )}
              {/* Every payment and refund, each with its own Refund. */}
              <PaymentHistory payments={a.payments} canRefund={refundable} onRefund={(p) => setRefunding(refundTarget(p))} />
              {/* Add discount is in More; once there is one, it shows here. */}
              {discountRow(a) && (
                <div className="appt-detail-item">
                  <span className="detail-label">Discount</span>
                  <span className="detail-value">
                    {!canTakeDiscount(a) ? (
                      discountRow(a)
                    ) : (
                      <span className="appt-discount">
                        <button type="button" className="btn btn-quiet btn-compact appt-discount__label" onClick={() => setDiscountOpen(true)}>{discountRow(a)}</button>
                        <button type="button" className="btn btn-quiet btn-compact appt-discount__remove" aria-label="Remove discount" disabled={busy} onClick={removeDiscount}>✕</button>
                      </span>
                    )}
                  </span>
                </div>
              )}
              {a.status === 'completed' && (
                <div className="appt-detail-item">
                  <span className="detail-label">Status</span>
                  <span className="detail-value">Completed</span>
                </div>
              )}
              {a.status === 'no_show' && (
                <div className="appt-detail-item">
                  <span className="detail-label">Status</span>
                  <span className="detail-value">No-show{a.paid_pence > 0 ? ' · deposit kept' : ''}</span>
                </div>
              )}
              {a.status === 'cancelled' && (
                <div className="appt-detail-item">
                  <span className="detail-label">Cancelled</span>
                  <span className="detail-value">{a.cancellation_reason ?? 'No reason given'}</span>
                </div>
              )}
              {/* An imported booking still to come; imported history says Paid before Harmovi instead. */}
              {a.source === 'import' && a.status === 'booked' && <span className="tag">Booked in Fresha</span>}
              {a.outside_hours && <span className="tag">Outside your working hours</span>}
              {/* Add appointment note is in More; a note, or one being
                  written, shows here. The heading labels the textarea, so it
                  isn't named twice (owner, 2026-10-31). */}
              {(a.status === 'completed' || a.status === 'booked') && client && (note || addingNote) && (
                <div className="appt-note">
                  <h3 className="booking-label" id="appt-note-heading">Appointment notes</h3>
                  {note && !addingNote ? (
                    <>
                      <p className="appt-note__text">{note.text}</p>
                      <div className="appt-note__photos">
                        {((note.photos?.length ?? 0) > 0 || photoQueue.items.length > 0) && (
                          <NotePhotoGrid clientId={client.id} noteId={note.id} photos={note.photos ?? []} onRemoved={notePhotoRemoved}>
                            <QueuedPhotoTiles
                              items={photoQueue.items}
                              onRemove={photoQueue.remove}
                              onRetry={(key) => photoQueue.retry(key, client.id, note.id, notePhotoAdded(note.id))}
                            />
                          </NotePhotoGrid>
                        )}
                        {photoQueue.problem && <p className="field-error" role="alert">{photoQueue.problem}</p>}
                        <AddNotePhoto
                          client={client}
                          left={photosLeft(note.photos?.length ?? 0, photoQueue.items.length)}
                          businessName={businessName}
                          onPicked={(photo) => {
                            // On a saved note a photo goes up as soon as it is picked.
                            photoQueue.add(photo)
                            photoQueue.upload(client.id, note.id, notePhotoAdded(note.id))
                          }}
                          onConsentRecorded={photoConsentRecorded}
                        />
                      </div>
                    </>
                  ) : addingNote ? (
                    <div className="appt-note__editor">
                      <textarea
                        id="appt-note-text"
                        aria-labelledby="appt-note-heading"
                        aria-describedby={noteError ? 'appt-note-error' : undefined}
                        aria-invalid={noteError ? true : undefined}
                        rows={3}
                        data-autofocus
                        autoFocus
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                      />
                      {noteError && (
                        <p id="appt-note-error" className="field-error" role="alert">
                          {noteError}
                        </p>
                      )}
                      {/* Photos picked now wait here and go up once the note is saved. */}
                      {photoQueue.items.length > 0 && (
                        <ul className="note-photos">
                          <QueuedPhotoTiles items={photoQueue.items} onRemove={photoQueue.remove} />
                        </ul>
                      )}
                      <div className="booking-actions">
                        <button
                          type="button"
                          className="btn btn-secondary btn-compact"
                          onClick={() => {
                            photoQueue.clear()
                            setAddingNote(false)
                          }}
                          disabled={noteSaving}
                        >
                          Cancel
                        </button>
                        <button type="button" className="btn btn-primary btn-compact" onClick={saveNote} disabled={noteSaving}>
                          {noteSaving ? 'Saving…' : 'Save note'}
                        </button>
                        <AddNotePhoto
                          client={client}
                          left={photosLeft(0, photoQueue.items.length)}
                          businessName={businessName}
                          onPicked={photoQueue.add}
                          onConsentRecorded={photoConsentRecorded}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          )}

          {mode === 'complete' &&
            payStep(
              due,
              (method) => complete(method),
              <>
                {canUsePackageCredit && (
                  <button type="button" className="btn btn-secondary btn-block" onClick={() => complete('package')} disabled={busy}>
                    Use a package session
                  </button>
                )}
                <button type="button" className="btn btn-secondary btn-block" onClick={() => complete('not_yet')} disabled={busy}>
                  Not paid yet
                </button>
              </>,
            )}

          {!held && mode === 'view' && settleable && payingOutstanding &&
            payStep(a.owed_pence, (method) =>
              run(
                () => api.recordPayment(a.id, method, keys.key(`pay:${method}`)),
                () => `Marked as paid ${paymentMethodPhrase(method)}`,
                'This appointment is already paid.',
                undefined,
                `pay:${method}`,
              ),
            )}

          {mode === 'reschedule' && (
            <section>
              <h3 className="booking-label">New time</h3>
              <TimePicker
                serviceLengthId={a.service.service_length_id}
                date={date}
                start={start}
                excludeId={a.id}
                onDateChange={(d) => {
                  setDate(d)
                  setConflict(null)
                }}
                onStartChange={(m) => {
                  setStart(m)
                  setConflict(null)
                }}
              />
            </section>
          )}

          {mode === 'service' && (
            <section className="booking-section">
              <h3 className="booking-label">Treatment</h3>
              {services === null ? (
                <p className="field-hint">Loading treatments…</p>
              ) : (
                <div className="booking-options">
                  {services.map((s) => (
                    <React.Fragment key={s.id}>
                      <button
                        type="button"
                        className="booking-option"
                        aria-pressed={s.id === a.service.id}
                        disabled={busy || serviceNotInPackage(s)}
                        onClick={() => {
                          // One length: that is the change. Several: she picks one below.
                          if (s.lengths.length > 1) {
                            setPickingServiceId(s.id)
                          } else if (s.lengths[0].id !== bookedLengthId) {
                            changeLength(s.lengths[0].id, false)
                          }
                        }}
                      >
                        <span className="booking-option__title">
                          <span className="category-dot" style={{ background: s.colour }} aria-hidden="true" />
                          {s.name}
                        </span>
                        <span className="booking-option__meta">
                          {lengthSummary(s)}{serviceNotInPackage(s) ? ` · not in their ${a.package?.name} package` : ''}
                        </span>
                      </button>
                      {s.lengths.length > 1 && s.id === pickingServiceId && (
                        <LengthPicker
                          lengths={s.lengths}
                          unavailable={(l) => notInPackage(l.id)}
                          selected={
                            s.lengths.some((l) => l.id === pendingLengthId)
                              ? pendingLengthId
                              : s.id === a.service.id
                                ? bookedLengthId
                                : null
                          }
                          disabled={busy}
                          onSelect={(l) => {
                            if (l.id !== bookedLengthId) changeLength(l.id, false)
                          }}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </section>
          )}

          {mode === 'cancel' && (
            <div className="settings-form sheet-form">
              {depositOnCancel && <p className="cancel-deposit">{depositOnCancel.note}</p>}
              {depositOnCancel?.keptByDefault && (
                <label className="checkbox-row">
                  <input type="checkbox" checked={refundDeposit} onChange={(e) => setRefundDeposit(e.target.checked)} />
                  <span>Refund the deposit instead</span>
                </label>
              )}
              {!a.awaiting_deposit && new Date(a.starts_at).getTime() > Date.now() && (
                <p className="field-hint">
                  {clientEmail
                    ? `We’ll email ${a.client?.first_name ?? 'the client'} to say it’s cancelled.`
                    : `${a.client?.first_name ?? 'The client'} has no email address, so let them know yourself.`}
                </p>
              )}
              <Field id="cancel-reason" label="Reason (optional, only you see it)">
                <textarea id="cancel-reason" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
              </Field>
            </div>
          )}

          {conflict && (
            <div className="booking-conflict" role="alert">
              <p>{conflict}</p>
              <button
                type="button"
                className="btn btn-secondary btn-compact"
                disabled={busy}
                onClick={() => (mode === 'service' && pendingLengthId ? changeLength(pendingLengthId, true) : reschedule(true))}
              >
                Book anyway
              </button>
            </div>
          )}
          {error && (
            <p className="field-error" role="alert">
              {error}
            </p>
          )}
        </div>

        {held && (
          <div className="sheet-actions">
            <button type="button" className="btn btn-primary" onClick={release} disabled={busy}>
              {busy ? 'Releasing…' : 'Release this time'}
            </button>
          </div>
        )}
        {/* One main action, following the visit (design review P1): Complete
            before or at it, Take payment once it's done and unpaid, nothing
            once paid. Everything else is in More beside it (appointment sheet
            review, 2026-10-15), or in a quiet row when there is no main action. */}
        {!held && mode === 'view' && (main ? (
          <div className="sheet-main">
            {main}
            <MoreMenu groups={more} disabled={busy} onOpenChange={setMenuOpen} />
          </div>
        ) : more.some((g) => g.length > 0) && (
          <div className="sheet-more">
            {more.flat().map((item) => (
              <button key={item.label} type="button" className={item.danger ? 'btn btn-danger btn-compact' : 'btn btn-quiet btn-compact'} disabled={busy} onClick={item.onSelect}>
                {item.label}
              </button>
            ))}
          </div>
        ))}
        {mode === 'reschedule' && (
          <div className="sheet-actions">
            <button type="button" className="btn btn-secondary" onClick={() => openMode('view')} disabled={busy}>
              Back
            </button>
            <button type="button" className="btn btn-primary" onClick={() => reschedule(false)} disabled={busy}>
              {busy ? 'Saving…' : 'Save new time'}
            </button>
          </div>
        )}
        {mode === 'cancel' && (
          // The final confirm is the only solid red; keeping it is below
          // (docs/25: the same structure as the booking site's manage page).
          <div className="sheet-stack">
            <button
              type="button"
              className="btn btn-danger-solid btn-block"
              disabled={busy}
              onClick={() =>
                run(
                  () =>
                    api.cancelAppointment(
                      a.id,
                      a.version,
                      reason.trim() || null,
                      depositOnCancel?.keptByDefault ? refundDeposit : undefined,
                    ),
                  () => 'Appointment cancelled',
                )
              }
            >
              {busy ? 'Cancelling…' : 'Cancel appointment'}
            </button>
            <button type="button" className="btn btn-secondary btn-block" onClick={() => openMode('view')} disabled={busy}>
              Keep appointment
            </button>
          </div>
        )}
        {(mode === 'complete' || mode === 'service') && (
          <button type="button" className="btn btn-quiet btn-block" onClick={() => openMode('view')} disabled={busy}>
            Back
          </button>
        )}
        {refunding && (
          <RefundSheet
            paymentId={refunding.paymentId}
            maxPence={refunding.maxPence}
            recordedMethod={refunding.recordedMethod}
            toCard={refunding.toCard}
            onClose={() => setRefunding(null)}
            onDone={() => {
              setRefunding(null)
              dispatch({ type: 'APPOINTMENTS_CHANGED' })
              load()
              loadMail()
            }}
          />
        )}
        {discountOpen && (
          <DiscountSheet
            appointment={a}
            onClose={() => setDiscountOpen(false)}
            onSaved={() => {
              setDiscountOpen(false)
              dispatch({ type: 'APPOINTMENTS_CHANGED' })
              load()
            }}
            onStale={staleDiscount}
          />
        )}
        {chargeOpen && (
          <ChargeCardSheet
            target={chargeCompletes ? { appointment_id: a.id, complete: true } : { appointment_id: a.id }}
            amountPence={chargeAmount}
            card={chargeCard}
            firstName={a.client?.first_name ?? ''}
            emailed={!!client?.email}
            onClose={() => {
              setChargeOpen(false)
              // Charged from the Complete step: the visit is completed now.
              // Leaving the step only on close keeps the sheet, and its Paid
              // line, in place until she taps Done.
              if (chargePaid.current && mode === 'complete') setMode('view')
              load()
            }}
            onPaid={() => {
              chargePaid.current = true
              dispatch({ type: 'APPOINTMENTS_CHANGED' })
              load()
            }}
            onSendLink={() => {
              setChargeOpen(false)
              setPayLinkOpen(true)
            }}
          />
        )}
        {payLinkOpen && (
          <PayLinkSheet
            target={chargeCompletes ? { appointment_id: a.id, complete: true } : { appointment_id: a.id }}
            clientHasCard={savedCard ? usableCard(savedCard) : a.client_has_card}
            onClose={() => {
              setPayLinkOpen(false)
              reloadAfterPayLink()
            }}
            onPaid={() => {
              dispatch({ type: 'APPOINTMENTS_CHANGED' })
              reloadAfterPayLink()
            }}
          />
        )}
      </div>
    </div>
  )
}

// importantFirst lists health items first, keeping her order within each.
export function importantFirst<T extends { is_health_flag: boolean }>(items: T[]): T[] {
  return [...items.filter((p) => p.is_health_flag), ...items.filter((p) => !p.is_health_flag)]
}

function confirmationText(a: Appointment): string {
  const name = a.client?.first_name
  return `${name ? `Hi ${name}, y` : 'Y'}ou’re booked in for ${a.service.name} on ${formatLongDay(a.date)} at ${minutesToTime(a.start_minute)}. See you then!`
}
